import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram } from "lucide-react";

export default function Footer() {
  return (
    <footer className="bg-[#14213D] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <div className="w-10 h-10 rounded-lg bg-[#00B4D8] flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <div>
                <span className="font-heading font-bold text-lg">Medical Access</span>
                <span className="block text-xs text-gray-400 -mt-1">Inc</span>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              A national medical network dedicated to providing accessible, quality healthcare across America.
            </p>
            <p className="text-gray-400 text-sm mb-6">
              <a href="https://accessmedicalgroup.online" className="text-[#00B4D8] hover:underline">
                accessmedicalgroup.online
              </a>
            </p>
            <p className="text-gray-400 text-sm mb-6" data-testid="footer-npi-section">
              <span className="block mb-1">NPI Number:</span>
              <a
                href="https://npiregistry.cms.hhs.gov/provider-view/1215849732"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00B4D8] hover:underline font-medium"
                data-testid="footer-npi-link"
              >
                1215849732
              </a>
            </p>
            <div className="flex gap-3">
              {[Facebook, Twitter, Linkedin, Instagram].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-[#00B4D8] transition-colors"
                  data-testid={`social-${i}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: "Find a Provider", path: "/find-provider" },
                { name: "Our Services", path: "/services" },
                { name: "Locations", path: "/locations" },
                { name: "Careers", path: "/careers" },
                { name: "About Us", path: "/about" },
              ].map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="text-gray-400 hover:text-[#00B4D8] transition-colors text-sm"
                    data-testid={`footer-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* For Patients */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">For Patients</h4>
            <ul className="space-y-3">
              {[
                "Patient Portal",
                "Insurance Information",
                "Telehealth Services",
                "Request Appointment",
                "Medical Records",
              ].map((item) => (
                <li key={item}>
                  <a
                    href="#"
                    className="text-gray-400 hover:text-[#00B4D8] transition-colors text-sm"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#00B4D8] mt-0.5 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  16475 N Dallas Pky, Suite 745<br />
                  Addison, TX 75001
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#00B4D8] flex-shrink-0" />
                <a href="tel:1-866-841-2149" className="text-gray-400 hover:text-[#00B4D8] text-sm" data-testid="footer-phone">
                  (866) 841-2149
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#00B4D8] flex-shrink-0" />
                <a href="mailto:info@accessmedicalgroup.online" className="text-gray-400 hover:text-[#00B4D8] text-sm" data-testid="footer-email">
                  info@accessmedicalgroup.online
                </a>
              </li>
            </ul>
            <div className="mt-6 text-gray-400 text-sm">
              <p className="font-medium text-white mb-1">Hours of Operation</p>
              <p>Mon - Fri: 9:00 AM - 5:00 PM</p>
              <p>Sat - Sun: 11:00 AM - 4:00 PM</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © 2024 Medical Access Inc. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy-policy" className="text-gray-500 text-sm hover:text-white" data-testid="footer-privacy">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-gray-500 text-sm hover:text-white" data-testid="footer-terms">
                Terms of Service
              </Link>
              <a href="#" className="text-gray-500 text-sm hover:text-white" data-testid="footer-hipaa">
                HIPAA Notice
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
